import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Util } from "../../../util/Util";
import { useAuth } from "../../../hooks/useAuth";
import { useSessionState } from "../../../hooks/useSessionState";
import { useData } from "../../../hooks/useData";
import { useUI } from "../../../providers/MoleUIContext";
import {
	Box,
	IconButton,
	Skeleton,
	Tooltip,
	Grid,
	Card,
	CardContent,
	Typography,
	Chip,
	Paper,
	TextField,
	InputAdornment,
	ToggleButtonGroup,
	ToggleButton,
	Stack,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import {
	Refresh,
	Search,
	SearchOff,
	Inventory2,
	ViewComfy,
	ViewModule,
	PictureAsPdf,
} from "@mui/icons-material";
import { LocalStorageKeys } from "../../../util/constants";
import Detail from "./Detail";
import InventoryCard from "./InventoryCard";
import ItemImageModal from "./ItemImageModal";
import { useTranslation } from "react-i18next";
import HubBackButton from "../../hubs/HubBackButton";
import { canExportPdf } from "../../../util/access";

type ViewDensity = "comfortable" | "compact";

const CurrentInventoryVisual = () => {
	const { t } = useTranslation("common");
	// Filters kept for the session (the page unmounts on tab switch).
	const [selectedDepot, setSelectedDepot] = useSessionState<any>(`depot:${LocalStorageKeys.CURRENT_INVENTORY_VISUAL}`, null);
	const [params, setParams] = useState({});
	const [selectedItem, setSelectedItem] = useState<any | null>(null);
	const [searchQuery, setSearchQuery] = useSessionState(`search:${LocalStorageKeys.CURRENT_INVENTORY_VISUAL}`, "");
	const [selectedCategories, setSelectedCategories] = useSessionState<string[]>(`categories:${LocalStorageKeys.CURRENT_INVENTORY_VISUAL}`, []);
	const [selectedSectors, setSelectedSectors] = useSessionState<string[]>(`sectors:${LocalStorageKeys.CURRENT_INVENTORY_VISUAL}`, []);
	const [viewDensity, setViewDensity] = useSessionState<ViewDensity>(`density:${LocalStorageKeys.CURRENT_INVENTORY_VISUAL}`, "comfortable");

	const { api, access } = useAuth();
	const { setLoadingMessage, queueReRun } = useUI();
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, loading } = useData(LocalStorageKeys.CURRENT_INVENTORY_VISUAL);

	const ITEMS_PER_PAGE = viewDensity === "comfortable" ? 24 : 48;
	const [page, setPage] = useSessionState(`page:${LocalStorageKeys.CURRENT_INVENTORY_VISUAL}`, 1);
	const pageResetReadyRef = useRef(false);

	// Derived filter options
	const categories = useMemo(() => {
		const set = new Set<string>();
		data.forEach((item: any) => { if (item.category) set.add(item.category); });
		return Array.from(set).sort();
	}, [data]);

	const sectors = useMemo(() => {
		const set = new Set<string>();
		data.forEach((item: any) => { if (item.sector) set.add(item.sector); });
		return Array.from(set).sort();
	}, [data]);

	// Filtered data
	const filteredData = useMemo(() => {
		let result = data;
		if (searchQuery) {
			const q = searchQuery.toLowerCase();
			result = result.filter((item: any) =>
				(item.item_name?.toLowerCase().includes(q)) ||
				(item.item_code?.toLowerCase().includes(q)) ||
				(item.category?.toLowerCase().includes(q))
			);
		}
		if (selectedCategories.length > 0) {
			result = result.filter((item: any) => selectedCategories.includes(item.category));
		}
		if (selectedSectors.length > 0) {
			result = result.filter((item: any) => selectedSectors.includes(item.sector));
		}
		return result;
	}, [data, searchQuery, selectedCategories, selectedSectors]);

	// Paginated data
	const paginatedData = useMemo(() => {
		const start = (page - 1) * ITEMS_PER_PAGE;
		return filteredData.slice(start, start + ITEMS_PER_PAGE);
	}, [filteredData, page, ITEMS_PER_PAGE]);

	const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

	// Reset page on filter change
	useEffect(() => {
		// Not on mount: a restored page belongs to the restored filters.
		if (!pageResetReadyRef.current) {
			pageResetReadyRef.current = true;
			return;
		}
		setPage(1);
	}, [searchQuery, selectedCategories, selectedSectors, viewDensity]);

	const handleSelectorChange = useCallback((value: Record<string, any>) => {
		setParams(value);
	}, []);

	const handleSetSelectedItem = useCallback((item: any) => {
		setSelectedItem(item);
	}, []);

	useEffect(() => {
		loadFromIndexedDB();
	}, []);

	const fetchData = useCallback(async () => {
		setLoadingMessage("FETCH");

		const res = await api.get(`inventory/${selectedDepot?.id}`, {
			params: params
		});

		if (res) {
			if (res.status === 200) {
				saveFromServer(res.data, undefined, true);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setLoadingMessage(null);
	}, [selectedDepot, params]);

	const handlePrint = useCallback(async () => {
		if (!canExportPdf(access, "inventory_current")) return;
		setLoadingMessage("EXPORT");

		const res = await api.get(`reports/inventory/visual/${selectedDepot?.id}`, {
			params: params
		});

		if (res) {
			if (res.status === 200) {
				const pdfData = atob(res.data.data);
				const blob = new Blob([Uint8Array.from(pdfData, c => c.charCodeAt(0))], { type: "application/pdf" });
				window.open(URL.createObjectURL(blob));
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setLoadingMessage(null);
	}, [selectedDepot, params]);

	const toggleCategory = (cat: string) => {
		setSelectedCategories(prev =>
			prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
		);
	};

	const toggleSector = (sec: string) => {
		setSelectedSectors(prev =>
			prev.includes(sec) ? prev.filter(s => s !== sec) : [...prev, sec]
		);
	};

	const gridSize = viewDensity === "comfortable"
		? { xs: 12, sm: 6, md: 4, lg: 3 }
		: { xs: 12, sm: 6, md: 3, lg: 2 };

	return (
		<>
			<Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
				{/* Header Area */}
				<Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Box>
						<HubBackButton />
						<Typography variant="h5" fontWeight="700">{t("inventory.gallery")}</Typography>
						<Typography variant="body2" color="text.secondary">
							{t("inventory.showingItems", {
								count: filteredData.length,
								depot: selectedDepot?.DepotName || t("inventory.depot"),
							})}
						</Typography>
					</Box>

					<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
						<Detail
							selectedDepot={selectedDepot}
							setSelectedDepot={setSelectedDepot}
							setParams={handleSelectorChange}
						/>
						<Tooltip title={t("ui.tooltips.refreshData")}>
							<IconButton onClick={fetchData} sx={{ border: '1px solid #e0e0e0' }}><Refresh /></IconButton>
						</Tooltip>
						{canExportPdf(access, "inventory_current") && (
							<Tooltip title={t("ui.buttons.export")}>
								<IconButton onClick={handlePrint} sx={{ border: '1px solid #e0e0e0' }}><PictureAsPdf /></IconButton>
							</Tooltip>
						)}
					</Box>
				</Paper>

				{/* Search & Filter Toolbar */}
				<Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
					<Stack spacing={2}>
						<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
							<TextField
								size="small"
								placeholder={t("inventory.searchPlaceholder")}
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								sx={{ flexGrow: 1, maxWidth: 400 }}
								slotProps={{
									input: {
										startAdornment: (
											<InputAdornment position="start">
												<Search />
											</InputAdornment>
										),
									}
								}}
							/>
							<ToggleButtonGroup
								size="small"
								value={viewDensity}
								exclusive
								onChange={(_, val) => val && setViewDensity(val)}
							>
								<ToggleButton value="comfortable">
									<Tooltip title={t("inventory.comfortable")}><ViewModule /></Tooltip>
								</ToggleButton>
								<ToggleButton value="compact">
									<Tooltip title={t("inventory.compact")}><ViewComfy /></Tooltip>
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>

						{(categories.length > 0 || sectors.length > 0) && (
							<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
								{categories.map(cat => (
									<Chip
										key={cat}
										label={cat}
										size="small"
										variant={selectedCategories.includes(cat) ? "filled" : "outlined"}
										color={selectedCategories.includes(cat) ? "primary" : "default"}
										onClick={() => toggleCategory(cat)}
									/>
								))}
								{sectors.map(sec => (
									<Chip
										key={sec}
										label={sec}
										size="small"
										variant={selectedSectors.includes(sec) ? "filled" : "outlined"}
										color={selectedSectors.includes(sec) ? "secondary" : "default"}
										onClick={() => toggleSector(sec)}
									/>
								))}
							</Box>
						)}
					</Stack>
				</Paper>

				{/* Gallery Grid */}
				<Grid container spacing={3}>
					{loading ? (
						Array.from({ length: 12 }).map((_, i) => (
							<Grid size={gridSize} key={i}>
								<Card sx={{ height: '100%' }}>
									<Box sx={{ pt: '100%', bgcolor: 'grey.100' }} />
									<CardContent>
										<Skeleton variant="text" width="60%" />
										<Skeleton variant="text" width="85%" />
										<Skeleton variant="text" width="40%" />
									</CardContent>
								</Card>
							</Grid>
						))
					) : paginatedData.length === 0 ? (
						<Box sx={{ width: '100%', textAlign: 'center', py: 8 }}>
							{data.length === 0 ? (
								<>
									<Inventory2 sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
									<Typography variant="h6" color="text.secondary">
										{t("inventory.selectDepotPrompt")}
									</Typography>
								</>
							) : (
								<>
									<SearchOff sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
									<Typography variant="h6" color="text.secondary">
										{t("inventory.noItemsFound")}
									</Typography>
									<Typography variant="body2" color="text.disabled">
										{t("inventory.adjustFilters")}
									</Typography>
								</>
							)}
						</Box>
					) : (
						paginatedData.map((item: any) => (
							<Grid size={gridSize} key={item.item_id}>
								<InventoryCard item={item} setSelectedItem={handleSetSelectedItem} />
							</Grid>
						))
					)}
				</Grid>

				{totalPages > 1 && (
					<>
						<Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 1 }}>
							<Pagination
								count={totalPages}
								page={page}
								onChange={(_, value) => setPage(value)}
								color="primary"
								size="large"
								showFirstButton
								showLastButton
							/>
						</Box>

						<Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
							{t("inventory.showingRange", {
								from: (page - 1) * ITEMS_PER_PAGE + 1,
								to: Math.min(page * ITEMS_PER_PAGE, filteredData.length),
								total: Util.formatNumber(filteredData.length),
							})}
						</Typography>
					</>
				)}
			</Box>

			{selectedItem &&
				<ItemImageModal
					isOpen={!!selectedItem}
					toggle={() => setSelectedItem(null)}
					item={selectedItem}
				/>
			}
		</>
	);
};

export default CurrentInventoryVisual;
